import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../components/Admin/AdminContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  Users, MapPin, Activity, AlertTriangle, TrendingUp, 
  Eye, Shield, Clock, BarChart3, UserCheck, UserX,
  Zap, Database, Server, Wifi
} from 'lucide-react';

export default function AdminDashboard() {
  const { adminAPI } = useAdmin();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getDashboard();
      setDashboard(data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      setError('Erreur lors du chargement du tableau de bord');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Chargement du tableau de bord...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 font-medium">{error}</p>
          <Button onClick={loadDashboard} variant="outline" className="mt-4">
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  const stats = dashboard?.stats;

  const statCards = [
    {
      title: 'Utilisateurs Totaux',
      value: stats?.total_users || 0,
      subValue: `${stats?.active_users || 0} actifs`,
      icon: Users,
      color: 'blue',
      trend: `+${stats?.new_users_today || 0} aujourd'hui`
    },
    {
      title: 'Spots Totaux',
      value: stats?.total_spots || 0,
      subValue: `${stats?.active_spots || 0} actifs`,
      icon: MapPin,
      color: 'green',
      trend: `+${stats?.new_spots_today || 0} aujourd'hui`
    },
    {
      title: 'Visites Totales',
      value: stats?.total_visits || 0,
      subValue: 'Enregistrées',
      icon: Activity,
      color: 'purple',
      trend: 'Cette semaine'
    },
    {
      title: 'Utilisateurs Suspendus',
      value: stats?.suspended_users + stats?.banned_users || 0,
      subValue: `${stats?.banned_users || 0} bannis`,
      icon: UserX,
      color: 'red',
      trend: 'Modération'
    }
  ];

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-yellow-100 text-yellow-800',
      banned: 'bg-red-100 text-red-800',
      inactive: 'bg-gray-100 text-gray-800'
    };
    
    return variants[status] || variants.active;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tableau de Bord</h1>
          <p className="text-gray-600 mt-1">Vue d'ensemble de la plateforme Sun-Spot</p>
        </div>
        <div className="flex items-center gap-2">
          <Wifi className="h-4 w-4 text-green-500" />
          <span className="text-sm text-gray-600">Système opérationnel</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="relative overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-2">{stat.value.toLocaleString()}</p>
                    <p className="text-sm text-gray-500 mt-1">{stat.subValue}</p>
                  </div>
                  <div className={`p-3 rounded-full bg-${stat.color}-100`}>
                    <Icon className={`h-6 w-6 text-${stat.color}-600`} />
                  </div>
                </div>
                <div className="mt-4 flex items-center">
                  <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-gray-600">{stat.trend}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              Utilisateurs Récents
            </CardTitle>
            <CardDescription>Dernières inscriptions sur la plateforme</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {dashboard?.recent_users?.slice(0, 5).map((user, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Users className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">@{user.username}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className={getStatusBadge(user.user_status || 'active')}>
                      {user.user_status || 'active'}
                    </Badge>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(user.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              )) || (
                <p className="text-gray-500 text-center py-4">Aucun utilisateur récent</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Spots */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Spots Récents
            </CardTitle>
            <CardDescription>Derniers spots ajoutés à la plateforme</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {dashboard?.recent_spots?.slice(0, 5).map((spot, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <MapPin className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{spot.title}</p>
                      <p className="text-sm text-gray-500">Par {spot.author}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className={getStatusBadge(spot.spot_status || 'active')}>
                      {spot.spot_status || 'active'}
                    </Badge>
                    <p className="text-xs text-gray-500 mt-1">
                      ❤️ {spot.likes || 0}
                    </p>
                  </div>
                </div>
              )) || (
                <p className="text-gray-500 text-center py-4">Aucun spot récent</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Most Liked Spots */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Spots les Plus Populaires
          </CardTitle>
          <CardDescription>Top des spots avec le plus de likes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats?.most_liked_spots?.slice(0, 6).map((spot, index) => (
              <div key={index} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">#{index + 1}</span>
                  <Badge variant="outline">❤️ {spot.likes}</Badge>
                </div>
                <h4 className="font-medium text-gray-900 truncate">{spot.title}</h4>
                <p className="text-sm text-gray-500 mt-1">ID: {spot.id}</p>
              </div>
            )) || (
              <p className="text-gray-500 text-center py-4 col-span-full">Aucune donnée disponible</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recent Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Actions Récentes d'Administration
          </CardTitle>
          <CardDescription>Dernières actions effectuées par les administrateurs</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {dashboard?.recent_actions?.slice(0, 8).map((action, index) => (
              <div key={index} className="flex items-center justify-between p-3 border-l-4 border-blue-200 bg-blue-50 rounded-r-lg">
                <div className="flex items-center gap-3">
                  <Shield className="h-4 w-4 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {action.admin_username} - {action.action_type}
                    </p>
                    <p className="text-xs text-gray-600">
                      {action.target_type}: {action.target_id}
                    </p>
                    {action.reason && (
                      <p className="text-xs text-gray-500 mt-1">Raison: {action.reason}</p>
                    )}
                  </div>
                </div>
                <span className="text-xs text-gray-500">
                  {new Date(action.created_at).toLocaleString('fr-FR')}
                </span>
              </div>
            )) || (
              <p className="text-gray-500 text-center py-4">Aucune action récente</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}